import React, { useEffect, useRef, useState } from "react";
import "../css/HomeScreen.css";

import HeroSection from "../components/HeroSection";
import BottomNav from "../components/BottomNav";
import Categories from "../components/Categories";
import RadiusSelector from "../components/RadiusSelector";
import NearByDealsSection from "../components/NearByDealsSection";

import { fetchHomeOffers } from "../apiIntegration/getOfferDataFromFirebase";

import { useQuery, useQueryClient } from "@tanstack/react-query";

export default function HomeScreen() {
	const queryClient = useQueryClient();

	const [activeTab, setActiveTab] = useState("home");

	const [userLocation, setUserLocation] = useState(null);

	const [locationLoading, setLocationLoading] = useState(true);

	const [locationDenied, setLocationDenied] = useState(false);

	const [isInstagramBrowser, setIsInstagramBrowser] = useState(false);

	const [showBrowserPopup, setShowBrowserPopup] = useState(false);

	const alertShownRef = useRef(false);

	const [distanceKm, setDistanceKm] = useState(
		() => queryClient.getQueryData(["distanceKm"]) || 5,
	);

	const [selectedCategory, setSelectedCategory] = useState(
		() => queryClient.getQueryData(["selectedCategory"]) || "all",
	);

	const [selectedSubCategory, setSelectedSubCategory] = useState(
		() => queryClient.getQueryData(["selectedSubCategory"]) || "all",
	);

	/* ====================================================== */

	useEffect(() => {
		queryClient.setQueryData(["distanceKm"], distanceKm);

		queryClient.setQueryData(["selectedCategory"], selectedCategory);

		queryClient.setQueryData(["selectedSubCategory"], selectedSubCategory);
	}, [distanceKm, selectedCategory, selectedSubCategory, queryClient]);

	/* ====================================================== */

	/* detect instagram browser and redirect to chrome */
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});

		const isInstagram = /Instagram/i.test(navigator.userAgent);

		if (isInstagram) {
			setIsInstagramBrowser(true);

			setShowBrowserPopup(true);

			return;
		}

		requestLocation();
	}, []);

	const continueInsideInstagram = () => {
		setShowBrowserPopup(false);

		requestLocation();
	};

	const openInBrowser = () => {
		const ua = navigator.userAgent;

		/* Android */
		if (/Android/i.test(ua)) {
			window.location.href =
				`intent://${window.location.host}${window.location.pathname}` +
				`#Intent;scheme=https;package=com.android.chrome;end`;

			return;
		}

		/* iPhone */
		alert("Tap ⋯ (top right) → Open in Browser");
	};

	/* ======================================================
	   LOCATION
	====================================================== */

	const requestLocation = () => {
	if (!navigator.geolocation) {
		setLocationLoading(false);
		setLocationDenied(true);

		alert("Location is not supported by this browser.");
		return;
	}

	/* Use React Query cache */

	const cached = queryClient.getQueryData(["userLocation"]);

	if (cached) {
		setUserLocation(cached);
		setLocationDenied(false);
		setLocationLoading(false);

		return;
	}

	setLocationLoading(true);
	setLocationDenied(false);

	navigator.geolocation.getCurrentPosition(
		(position) => {
	const location = {
		lat: Number(position.coords.latitude.toFixed(3)),
		lng: Number(position.coords.longitude.toFixed(3)),
	};

	console.log("LOCATION SUCCESS:", location);
	console.log("LOCATION ACCURACY:", position.coords.accuracy);

	setUserLocation(location);

	queryClient.setQueryData(["userLocation"], location);

	setLocationDenied(false);
	alertShownRef.current = false;
	setLocationLoading(false);
},

		(error) => {
			console.log("LOCATION ERROR:", {
				code: error.code,
				message: error.message,
			});

			setLocationLoading(false);
			setLocationDenied(true);

			if (!alertShownRef.current) {
				alertShownRef.current = true;

				setTimeout(() => {
					if (error.code === 1) {
						alert(
							"Location permission is blocked. Please allow location access for OfferNova in your browser settings.",
						);
					} else if (error.code === 2) {
						alert(
							"Your device could not determine your location. Please make sure Location/GPS is ON and try again.",
						);
					} else if (error.code === 3) {
						alert(
							"Getting your location is taking too long. Please try again.",
						);
					} else {
						alert(
							"Unable to get your location. Please try again.",
						);
					}
				}, 500);
			}
		},

		{
			enableHighAccuracy: true,
			maximumAge: 5 * 60 * 1000,
			timeout: 20000,
		},
	);
};

	/* ====================================================== */

	const {
		data: homeData,

		isLoading,

		isFetching,

		error,
	} = useQuery({
		queryKey: [
			"homeOffers",

			userLocation?.lat,

			userLocation?.lng,

			distanceKm,

			selectedCategory,

			selectedSubCategory,
		],

		queryFn: () =>
			fetchHomeOffers(
				userLocation,

				distanceKm,

				selectedCategory,

				selectedSubCategory,
			),

		enabled: !!userLocation,

		keepPreviousData: true,

		staleTime: 1000 * 60 * 30,

		cacheTime: 1000 * 60 * 60 * 2,

		refetchOnMount: false,

		refetchOnWindowFocus: false,

		refetchOnReconnect: false,
	});

	useEffect(() => {
		if (error) {
			console.log(error);
		}
	}, [error]);

	const categorySections = homeData?.categorySections || [];

	const categories = homeData?.categories || [];

	const isPageLoading = locationLoading || isLoading || isFetching;

	return (
		<div className="app-container">
			{showBrowserPopup && (
				<div
					className="
						location-modal-overlay"
				>
					<div
						className="
							location-modal"
					>
						<h5>Open OfferNova in Browser</h5>

						<p>Instagram may repeatedly request location.</p>

						<button
							className="
								btn
								btn-dark
								w-100"
							onClick={openInBrowser}
						>
							Open in Browser
						</button>

						<button
							className="
								btn
								btn-outline-light
								w-100
								mt-2"
							onClick={continueInsideInstagram}
						>
							Continue Here
						</button>
					</div>
				</div>
			)}
			<HeroSection />
			<Categories
				categories={categories}
				selectedCategory={selectedCategory}
				setSelectedCategory={setSelectedCategory}
				selectedSubCategory={selectedSubCategory}
				setSelectedSubCategory={setSelectedSubCategory}
			/>

			<div className="mt-3">
				<RadiusSelector radius={distanceKm} setRadius={setDistanceKm} />
			</div>

			{/* LOCATION BLOCK */}

			{locationDenied && !userLocation && (
				<div
					className="
						text-center
						p-4"
				>
					<h6>Location required</h6>
					<br />
					<p>
						Enable device location from settings to discover nearby
						offers for you.
					</p>

					<button
						className="btn btn-primary"
						onClick={requestLocation}
					>
						Try Again
					</button>
				</div>
			)}

			<NearByDealsSection
				categorySections={categorySections}
				loading={isPageLoading}
				distanceKm={distanceKm}
			/>

			<BottomNav active={activeTab} setActive={setActiveTab} />
		</div>
	);
}
